/* TODO Clean-up after the PDP Overhaul test.
   It's duplicated from config-simple-selectors for the test.
*/
import { parse, stringify } from 'qs';
import { closest, Component, element as $, elements as $$, services } from '@contorion/core';
import { IImageOverlayService } from '../interfaces/image-overlay-service.interface';
import { IProductService } from '../interfaces/product-service.interface';
import { PRODUCTS } from '../constants/products.constants';
import { algoliaService } from '../../algolia/services/algolia.service';

const CLASS_SELECT_DISABLED: string = 'a-config-variant-v2--disabled';
const CLASS_SELECTED: string = 'a-config-variant-v2__input--button-selected';
const SELECTOR_VARIANT_INPUT: string = '.js-config-variant-input';
const SELECTOR_VARIANT_BUTTON: string = '.js-config-variant-button';
const SELECTOR_VARIANT_BUTTON_WRAPPER: string = '.js-config-variant-button-wrapper';
const SIMPLE_SKU_PROPERTY: string = 'simple_sku';
const ATTRIBUTE_DISABLED: string = 'disabled';
const KEY_RELATION_SKU: string = 'relation_sku';

export class ConfigSimpleSelectorsV2Component extends Component {
    protected productService: IProductService;
    protected imageOverlayService: IImageOverlayService;

    protected get relationSku(): string {
        return this.element.dataset.relationSku;
    }

    protected get simples(): Array<any> {
        const result: Array<any> = [];
        const simples: any = JSON.parse(this.element.dataset.simples);

        for (const sku in simples) {
            if (!simples.hasOwnProperty(sku)) {
                continue;
            }

            const simple: any = simples[sku];
            simple.sku = sku;
            result.push(simple);
        }

        return result;
    }

    protected get ref(): string {
        return this.element.dataset.ref || '';
    }

    protected get baseUrl(): string {
        return this.element.dataset.baseUrl;
    }

    protected get relationParam(): string {
        return this.relationSku && `${KEY_RELATION_SKU}=${this.relationSku}`;
    }

    protected get selectors(): Array<HTMLSelectElement | HTMLInputElement> {
        return $$(SELECTOR_VARIANT_INPUT, this.element)
            .map((element: HTMLSelectElement | HTMLInputElement) => element);
    }

    protected get buttons(): Array<HTMLButtonElement> {
        return $$<HTMLButtonElement>(SELECTOR_VARIANT_BUTTON);
    }

    protected get notAvailableText(): string {
        return ' ' + this.element.dataset.notAvailableText;
    }

    constructor(protected element: HTMLFormElement) {
        super(element);

        this.init();
    }

    protected async init(): Promise<void> {
        this.productService = await services.get('product-service');
        this.imageOverlayService = await services.get('image-overlay-service');
        this.bindEvents();
        this.toggleAvailableOptions();

        if (this.selectors.length === 2 && this.selectors[0].value && !this.selectors[1].value) {
            this.handleSelection(this.selectors[0]);
        }
    }

    protected bindEvents(): void {
        for (const selector of this.selectors) {
            selector.addEventListener('change', this.onSelectorChange.bind(this));
        }

        for (const button of this.buttons) {
            button.addEventListener('click', this.onButtonClick.bind(this));
        }

        this.element.addEventListener('mousemove', this.onMouseMove.bind(this));
        this.element.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    }

    protected handleSelection(selector: HTMLSelectElement | HTMLInputElement): void {
        const selectorsCount: number = this.selectors.length;
        const isFirstButNotOnly: boolean = selectorsCount > 1 && selector === this.selectors[0];
        const isEmptyValue: boolean = selector.value === '';
        if (isEmptyValue) {
            const vct = this.element.dataset.ref ? '?vct=' + this.element.dataset.ref : '';
            let url: string = this.element.dataset.baseUrl + vct;
            if (algoliaService.isAlgoliaSearch()) {
                if (url.includes('?')) {
                    url += '&queryId=' + algoliaService.getAlgoliaQueryId();
                } else {
                    url += '?queryId=' + algoliaService.getAlgoliaQueryId();
                }
            }

            this.loadUrl(url);
            return;
        }

        if (isFirstButNotOnly) {
            this.enableSelector(this.selectors[1]);
            this.toggleAvailableOptions();
            this.checkSelections();
        }

        window.setTimeout(() => {
            if (this.checkValidity()) {
                this.changeToSimple();
            }
        });
    }

    protected checkValidity(): boolean {
        return this.selectors.filter((selector) => {
            return selector.value === '';
        }).length === 0;
    }

    protected enableSelector(selector: HTMLSelectElement | HTMLInputElement) {
        if (!selector.hasAttribute(ATTRIBUTE_DISABLED)) {
            return;
        }

        selector.removeAttribute(ATTRIBUTE_DISABLED);
        selector.parentElement.classList.remove(CLASS_SELECT_DISABLED);
    }

    protected disableSelector(selector: HTMLSelectElement | HTMLInputElement) {
        selector.setAttribute(ATTRIBUTE_DISABLED, ATTRIBUTE_DISABLED);
        selector.parentElement.classList.add(CLASS_SELECT_DISABLED);
    }

    protected toggleAvailableOptions(): void {
        if (this.selectors.length < 2) {
            return;
        }

        const buttonWrapper: Element = closest(this.selectors[1], SELECTOR_VARIANT_BUTTON_WRAPPER);

        if (!!buttonWrapper) {
            const buttons: Array<HTMLButtonElement> =
                $$<HTMLButtonElement>(SELECTOR_VARIANT_BUTTON, buttonWrapper);

            this.handleButtonsAvailability(buttons);

            return;
        }

        const options: Array<HTMLOptionElement> =
            $$<HTMLOptionElement>('option', this.selectors[1]);

        if (options && options.length > 0) {
            this.handleOptionsAvailability(options);

            return;
        }
    }

    protected handleOptionsAvailability(options: Array<HTMLOptionElement>): void {
        const availableValues: Array<any> = this.getAvailableValues();

        for (const option of options) {
            if (option.value === '' || availableValues.indexOf(option.value) !== -1) {
                option.removeAttribute(ATTRIBUTE_DISABLED);
                option.text = option.text.replace(this.notAvailableText, '');

                continue;
            }

            option.setAttribute(ATTRIBUTE_DISABLED, ATTRIBUTE_DISABLED);

            if (option.text.indexOf(this.notAvailableText) === -1) {
                option.text += this.notAvailableText;
            }
        }
    }

    protected handleButtonsAvailability(buttons: Array<HTMLButtonElement>): void {
        const availableValues: Array<any> = this.getAvailableValues();

        for (const button of buttons) {
            if (button.dataset.value === '' ||
                availableValues.indexOf(button.dataset.value) !== -1) {
                button.removeAttribute(ATTRIBUTE_DISABLED);

                continue;
            }

            button.setAttribute(ATTRIBUTE_DISABLED, ATTRIBUTE_DISABLED);
        }
    }

    protected checkSelections(): void {
        if (this.selectors.length < 2) {
            return;
        }

        const availableValues: Array<any> = this.getAvailableValues();
        const value: string = this.selectors[1].value;

        if (value.length && availableValues.indexOf(value) === -1) {
            this.selectors[1].value = '';
        }

        if (availableValues.length === 1) {
            this.selectors[1].value = availableValues[0];
        }
    }

    protected getAvailableValues(): Array<any> {
        const first: HTMLSelectElement | HTMLInputElement = this.selectors[0];
        const second: HTMLSelectElement | HTMLInputElement = this.selectors[1];
        const firstAttribute: string = first.getAttribute('data-variant-name');
        const secondAttribute: string = second.getAttribute('data-variant-name');
        const value: string = first.value;
        const availableValues: Array<any> = [];

        for (const simple of this.simples) {
            if (
                simple[firstAttribute] !== value ||
                availableValues.indexOf(simple[secondAttribute]) !== -1
            ) {
                continue;
            }

            availableValues.push(simple[secondAttribute]);
        }

        return availableValues;
    }

    protected getValues(): any {
        return this.selectors.reduce((accumulator, selector) => {
            return {
                ...accumulator,
                [selector.dataset.variantName]: selector.value,
            };
        }, {});
    }

    protected getRef(values: any): string {
        const refValues: Array<string> = [];

        for (const prop in values) {
            if (values.hasOwnProperty(prop)) {
                refValues.push(values[prop]);
            }
        }

        return this.ref + '_' + refValues.join('-');
    }

    protected getSimplesBySelection(values: any): Array<any> {
        return this.simples.filter((simple) => {
            for (const name in values) {
                if (!values.hasOwnProperty(name) || simple[name] !== values[name]) {
                    return false;
                }
            }

            return true;
        }).sort((a, b) => {
            if (!a[PRODUCTS.packageSizeProperty] || !b[PRODUCTS.packageSizeProperty]) {
                return 0;
            }

            return a[PRODUCTS.packageSizeProperty] - b[PRODUCTS.packageSizeProperty];
        });
    }

    /**
     * @TODO: SINGLE RESPONSIBILITES
     */
    protected changeToSimple(): void {
        const values: any = this.getValues();
        const selectedSimples: any = this.getSimplesBySelection(values);

        let packageSize: string = '';

        if (selectedSimples[0]) {
            values[SIMPLE_SKU_PROPERTY] = selectedSimples[0].sku;
            if (selectedSimples[0].$ps) {
                packageSize = '&' + PRODUCTS.packageSizeProperty + '=' + selectedSimples[0].$ps;
            }
        }

        this.handleImageOverlay(values, selectedSimples);

        if (this.ref.length) {
            values.vct = this.getRef(values);
        }

        if (algoliaService.isAlgoliaSearch()) {
            values.queryId = algoliaService.getAlgoliaQueryId();
        }

        const params: string = [
            stringify(values),
            this.relationParam
        ].filter((text) => text).join('&');

        const url: string = `${this.baseUrl}?${params}`;

        this.loadUrl(url);
    }

    protected handleImageOverlay(values: any, selectedSimples: any): void {
        if (!this.imageOverlayService.overlayUrl) {
            return;
        }

        const searchQuery: any = parse(
            this.imageOverlayService.overlayUrl.search.replace(
                '?',
                ''
            ));

        if (searchQuery.sku !== values[SIMPLE_SKU_PROPERTY]) {
            const overlayUrl: URL = this.imageOverlayService.overlayUrl;

            Object.assign(searchQuery, selectedSimples[0]);
            this.imageOverlayService.overlayUrl = new URL(
                `${overlayUrl.origin}${overlayUrl.pathname}?${stringify(searchQuery)}`
            );
        }
    }

    protected loadUrl(url: string): void {
        this.productService.setUrl(url);
    }

    protected toggleDisabledIndicator(hoveredElement: HTMLElement): void {
        if (closest(hoveredElement, `.${CLASS_SELECT_DISABLED}`)) {
            this.addDisabledIndicator();
        } else {
            this.removeDisabledIndicator();
        }
    }

    protected removeDisabledIndicator(): void {
        this.element.classList.remove('disabled-indicator');
    }

    protected addDisabledIndicator(): void {
        this.element.classList.add('disabled-indicator');
    }

    protected onSelectorChange(event: Event): void {
        event.preventDefault();

        this.handleSelection(<HTMLSelectElement> event.target);
    }

    protected onButtonClick(event: Event): void {
        event.preventDefault();

        const buttonElement = <HTMLButtonElement> closest(
            <HTMLElement> event.target,
            SELECTOR_VARIANT_BUTTON
        );
        const { target, value } = buttonElement.dataset;
        const hiddenInput: HTMLInputElement = this.getTargetInputFromElement(buttonElement, target);

        this.toggleSelectedClass(buttonElement, value);
        this.setHiddenInputValue(
            hiddenInput,
            value
        );
        this.handleSelection(hiddenInput);
    }

    protected getTargetInputFromElement(
        buttonElement: HTMLElement,
        target: string
    ): HTMLInputElement {
        return <HTMLInputElement> $(
            `input[data-variant-name=${target}]`, buttonElement.parentElement
        );
    }

    protected setHiddenInputValue(element: HTMLInputElement, value: string): void {
        element.value = value;
    }

    protected toggleSelectedClass(
        buttonElement: HTMLButtonElement,
        value: string
    ) {
        $$<HTMLButtonElement>(SELECTOR_VARIANT_BUTTON, buttonElement.parentElement)
            .map((button) => {
                if (button.dataset.value === value) {
                    button.classList.add(CLASS_SELECTED);

                    return;
                }

                button.classList.remove(CLASS_SELECTED);
            });
    }

    protected onMouseMove(e: Event): void {
        this.toggleDisabledIndicator(<HTMLElement> e.target);
    }

    protected onMouseLeave(): void {
        this.removeDisabledIndicator();
    }
}
